import MacroEvent from '@shared/models/deviceProfiles/MacroEvent';
import DeviceTypeThrottleRules, {
    IncompatibleActionGroup,
    MacroActionReference,
    RequiredPauseBetweenRule,
} from '@shared/models/devices/DeviceTypeThrottleRules';

function randomJitter(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

function actionMatchesRef(event: MacroEvent, ref: MacroActionReference): boolean {
    if (event.eventType !== ref.eventType) return false;
    if (ref.deviceTypeCustomActionId) {
        return event.deviceTypeCustomActionId === ref.deviceTypeCustomActionId;
    }
    if (ref.key) {
        return event.keys?.includes(ref.key) ?? false;
    }
    return true;
}

export function applyMacroJitter(events: MacroEvent[], rules: DeviceTypeThrottleRules): MacroEvent[] {
    return events.map((event) => {
        const updated = { ...event };

        if (event.holdTimeMilliseconds != null && event.holdTimeMilliseconds > 0) {
            let jitterMin: number | undefined;
            let jitterMax: number | undefined;

            const stepRule = rules.macroStepRules?.find(
                (sr) =>
                    sr.eventType === event.eventType &&
                    (sr.key == null || event.keys?.includes(sr.key)) &&
                    (sr.deviceTypeCustomActionId == null ||
                        sr.deviceTypeCustomActionId === event.deviceTypeCustomActionId)
            );

            if (stepRule?.holdTimeJitterMinMilliseconds != null && stepRule?.holdTimeJitterMaxMilliseconds != null) {
                jitterMin = stepRule.holdTimeJitterMinMilliseconds;
                jitterMax = stepRule.holdTimeJitterMaxMilliseconds;
            } else if (event.eventType === 'Pause') {
                if (rules.pauseTimeJitterMinMilliseconds != null && rules.pauseTimeJitterMaxMilliseconds != null) {
                    jitterMin = rules.pauseTimeJitterMinMilliseconds;
                    jitterMax = rules.pauseTimeJitterMaxMilliseconds;
                }
            } else {
                if (rules.holdTimeJitterMinMilliseconds != null && rules.holdTimeJitterMaxMilliseconds != null) {
                    jitterMin = rules.holdTimeJitterMinMilliseconds;
                    jitterMax = rules.holdTimeJitterMaxMilliseconds;
                }
            }

            if (jitterMin != null && jitterMax != null && jitterMax > 0) {
                const jitter = randomJitter(jitterMin, jitterMax);
                updated.holdTimeMilliseconds = Math.max(0, event.holdTimeMilliseconds + jitter);
            }
        }

        return updated;
    });
}

export function validateIncompatibleActions(
    events: MacroEvent[],
    groups: IncompatibleActionGroup[]
): IncompatibleActionGroup | undefined {
    for (const group of groups) {
        const allPresent = group.actions.every((ref) => events.some((e) => actionMatchesRef(e, ref)));
        if (allPresent) return group;
    }
    return undefined;
}

export function enforceRequiredPauses(events: MacroEvent[], pauseRules: RequiredPauseBetweenRule[]): MacroEvent[] {
    if (events.length < 2) return events;

    const result: MacroEvent[] = [events[0]];

    for (let i = 1; i < events.length; i++) {
        const prev = events[i - 1];
        const curr = events[i];

        let maxRequiredPause = 0;

        for (const rule of pauseRules) {
            const prevMatchesA = actionMatchesRef(prev, rule.actionA);
            const currMatchesB = actionMatchesRef(curr, rule.actionB);
            const forwardMatch = prevMatchesA && currMatchesB;

            const reverseMatch =
                rule.bidirectional && actionMatchesRef(prev, rule.actionB) && actionMatchesRef(curr, rule.actionA);

            if (forwardMatch || reverseMatch) {
                maxRequiredPause = Math.max(maxRequiredPause, rule.minimumPauseMilliseconds);
            }
        }

        if (maxRequiredPause > 0) {
            const existingPause = prev.eventType === 'Pause' ? (prev.holdTimeMilliseconds ?? 0) : 0;

            if (existingPause < maxRequiredPause) {
                if (prev.eventType === 'Pause') {
                    result[result.length - 1] = {
                        ...prev,
                        holdTimeMilliseconds: maxRequiredPause,
                    };
                } else {
                    result.push({
                        eventType: 'Pause',
                        holdTimeMilliseconds: maxRequiredPause,
                    });
                }
            }
        }

        result.push(curr);
    }

    return result;
}
